import { createContext, useContext, useEffect, useState, ReactNode } from 'react'
import { getToken } from '../lib/authToken'

interface TenantStatusContextValue {
  tenantStatus: string | null
  isSuspended: boolean
  isLoading: boolean
}

const TenantStatusContext = createContext<TenantStatusContextValue>({
  tenantStatus: null,
  isSuspended: false,
  isLoading: true,
})

// CSS injected into the document when body.tenant-suspended is set.
// Disables pointer events and grays out mutating action buttons.
const SUSPENDED_CSS = `
body.tenant-suspended button[type="submit"],
body.tenant-suspended button:has(.lucide-plus),
body.tenant-suspended button:has(.lucide-trash),
body.tenant-suspended button:has(.lucide-trash-2),
body.tenant-suspended button:has(.lucide-pencil),
body.tenant-suspended button:has(.lucide-edit),
body.tenant-suspended [data-action="delete"],
body.tenant-suspended [data-action="save"] {
  opacity: 0.5;
  pointer-events: none;
  cursor: not-allowed;
}
`

export function TenantStatusProvider({ children }: { children: ReactNode }) {
  const [tenantStatus, setTenantStatus] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  // Inject suspended CSS once on mount, remove on unmount.
  useEffect(() => {
    const styleEl = document.createElement('style')
    styleEl.setAttribute('data-tenant-suspended-styles', '')
    styleEl.textContent = SUSPENDED_CSS
    document.head.appendChild(styleEl)
    return () => styleEl.remove()
  }, [])

  // Toggle body class so CSS selectors can disable mutating elements.
  const isSuspended = tenantStatus === 'suspended'
  useEffect(() => {
    if (isSuspended) {
      document.body.classList.add('tenant-suspended')
    } else {
      document.body.classList.remove('tenant-suspended')
    }
    return () => document.body.classList.remove('tenant-suspended')
  }, [isSuspended])

  useEffect(() => {
    const token = getToken()
    if (!token) {
      setIsLoading(false)
      return
    }

    // Try to get tenant status from JWT first (no network call)
    try {
      const payload = JSON.parse(atob(token.split('.')[1]))
      if (payload.tenant_status) {
        setTenantStatus(payload.tenant_status)
        setIsLoading(false)
        return
      }
    } catch {
      // JWT decode failed, fall through to API call
    }

    // Fallback: fetch from API
    fetch('/api/tenants/current', {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((data) => {
        setTenantStatus(data.status ?? null)
      })
      .catch(() => {
        // Ignore errors — tenant status is best-effort for UI
      })
      .finally(() => {
        setIsLoading(false)
      })
  }, [])

  return (
    <TenantStatusContext.Provider
      value={{
        tenantStatus,
        isSuspended,
        isLoading,
      }}
    >
      {children}
    </TenantStatusContext.Provider>
  )
}

export function useTenantStatus() {
  return useContext(TenantStatusContext)
}
